import { useEffect, useState } from 'react';
import {
  getIframePoolOfferItems,
  getIframePoolOfferOdds,
  getPoolOffer,
  listAvailableCategories,
  type ApiClient,
  type AvailableCategory,
  type IframePoolItem,
  type IframePoolOfferOdds,
  type PoolOfferDetail,
} from '../../../modules/api';

export interface PackDataState {
  offer: PoolOfferDetail | null;
  categories: AvailableCategory[];
  odds: IframePoolOfferOdds | null;
  /**
   * Upper bound for the quantity stepper (backend `max_for_purchase_count`).
   * `undefined` while odds are still loading — consumers should treat it as
   * "no cap yet" and keep the stepper usable. A numeric `0` means the offer
   * is actually out of stock.
   */
  maxForPurchaseCount: number | undefined;
  /**
   * Per-operator override URL for the Pack-page background image. `null`
   * means no override — render the design_system gradient instead.
   */
  bgImageUrl: string | null;
  potentialHits: IframePoolItem[];
  potentialHitsLoading: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Parallel fetch for the pack-detail page:
 *   - offer detail + category pills (public shared namespace, no token)
 *   - odds + potential hits (iframe namespace, session-gated)
 *
 * Iframe-namespace calls fail soft: their panels just hide when the call
 * errors, so pages still render for unauthenticated preview.
 */
export function usePackData(client: ApiClient, gameId: string): PackDataState {
  const [offer, setOffer] = useState<PoolOfferDetail | null>(null);
  const [categories, setCategories] = useState<AvailableCategory[]>([]);
  const [odds, setOdds] = useState<IframePoolOfferOdds | null>(null);
  const [potentialHits, setPotentialHits] = useState<IframePoolItem[]>([]);
  const [potentialHitsLoading, setPotentialHitsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOdds(null);
    setPotentialHits([]);
    setPotentialHitsLoading(true);

    Promise.all([getPoolOffer(client, gameId), listAvailableCategories(client)])
      .then(([o, cats]) => {
        if (cancelled) return;
        setOffer(o);
        setCategories(cats);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load pack';
        setError(message);
        setLoading(false);
      });

    getIframePoolOfferOdds(client, gameId)
      .then((o) => {
        if (!cancelled) setOdds(o);
      })
      .catch(() => {
        /* odds are optional — panel hides if they don't load */
      });

    getIframePoolOfferItems(client, gameId)
      .then((page) => {
        if (cancelled) return;
        setPotentialHits(page.items);
        setPotentialHitsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPotentialHitsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, gameId]);

  return {
    offer,
    categories,
    odds,
    maxForPurchaseCount: odds ? odds.maxForPurchaseCount : undefined,
    bgImageUrl: odds?.bgImageUrl ?? null,
    potentialHits,
    potentialHitsLoading,
    loading,
    error,
  };
}
